import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
  MouseEvent,
} from "react";

import { menuInfo } from "@shared/type/CommonType";
import SnNavBtn from "@shared/components/SnNavBtn";
import { IconButton, Menu, MenuItem, Divider, ListItemIcon, ListItemText, ListSubheader } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { global } from "@shared/libs/Global";
import SnFormControls from "@shared/components/SnFormControls";
import {
  SnSortableFormArea,
  SnSortableFormControl,
  sortIdOf,
} from "@shared/components/SnSortableFormControls";
import { SnControlSelector } from "@shared/components/SnControlSelector";
import { showPopupModal } from "@widgets/CommonModal";
import { fnSave } from "@shared/api/restApi";

interface FreeFormProps {
  data: any;
  controls: any[];
  width?: number;
  // 화면별로 FreeForm grid column 폭 정책을 오버라이드한다. 기본값은 기존 13컬럼 최소폭 패턴을 유지한다.
  gridTemplateColumns?: string;
  // fitToPopup 미사용 시 펼침 높이(기본 500)를 오버라이드한다.
  panelMaxHeight?: number;
  // true일 때만 팝업 크기에 맞춰 폼 폭/높이를 자동 조정한다.
  fitToPopup?: boolean;
  /**
   * data 가 바뀔 때 첫 컨트롤로 포커스를 옮길지(기본 true = 기존 동작).
   * 화면 아래쪽에 있는 폼은 조회·신규마다 포커스를 빼앗아 화면이 아래로 튀므로 false 로 끈다.
   */
  autoFocus?: boolean;
  onButtonClick?: (sender: string) => void;
  onValueChanged?: (data: any, param: string, formData: any) => void;
  title?: string;
  header?: React.ReactNode;
  menuInfo?: menuInfo;
  popupRef?: any;
}

interface changesProps {
  state: string;
  keys: any;
  data: any;
}

type InputEls = {
  el: HTMLElement | null;
  isFocusable: boolean;
};

/** 우클릭 메뉴 제목 줄 — 작고 옅게 (2026-10-02, SearchPanel 과 같다) */
const menuHeadSx = { lineHeight: "28px", fontSize: 12, color: "#7d7e80", bgcolor: "transparent" };

const FreeForm = forwardRef(
  (
    {
      data,
      controls,
      width,
      gridTemplateColumns,
      panelMaxHeight,
      fitToPopup = false,
      autoFocus = true,
      onButtonClick,
      title,
      header,
      menuInfo,
      onValueChanged,
      popupRef,
    }: FreeFormProps,
    ref
  ) => {
    const { userInfo } = global();
    const [formData, setFormData] = useState<any>(data);
    const [visibleControls, setVisibleControls] = useState<any[]>([]);
    const [hiddenControls, setHiddenControls] = useState<any[]>([]);
    const [editCons, setEditCons] = useState<any[]>();
    const [editCon, setEditCon] = useState<any>();
    const [requireds, setRequireds] = useState<any>({});
    const [groups, setGroups] = useState<any[]>();
    const [filters, setFilters] = useState<any[]>();
    const [filter, setFilter] = useState<any>();
    const [changeData, setChangeData] = useState<any>(); // 변경된 데이터 관리용
    const [isExpand, setIsExpand] = useState(false);
    const expandedMaxHeight = panelMaxHeight ?? 500;
    const [panelHeight, setPanelHeight] = useState<number>(expandedMaxHeight);
    const expandcss = "overflow-hidden transition-all duration-500 ease-in-out";

    const formRef = useRef<HTMLDivElement>(null); // form 영역을 참조
    const [menuPosition, setMenuPosition] = useState<{
      mouseX: any;
      mouseY: any;
    } | null>(null);
    const refs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const inputEls = useRef<InputEls[]>([]);
    const [popupCons, setPopupCons] = useState<any>({}); /* */
    const formDataRef = useRef(formData);
    const changeDataRef = useRef(changeData);
    /* 배치 편집에 들어가기 직전의 칸 목록 — [편집 취소] 때 되돌린다 (2026-10-02) */
    const layoutSnapshotRef = useRef<{ visible: any[]; hidden: any[] } | null>(null);
    const [isDragOn, setIsDragOn] = useState(false);
    const [focus, setFocus] = useState<boolean>();

    const fetchControl = async () => {
      try {
        const eidtConFilters = controls.filter(
          (control) =>
            control.dc_editcon &&
            control.dc_editcon.length > 0 &&
            control.dc_editcon.trim() != ""
        );
        setEditCons(eidtConFilters);

        const filterFilters = controls.filter(
          (control) =>
            control.dc_filter &&
            control.dc_filter.length > 0 &&
            control.dc_filter.trim() != ""
        );
        setFilters(filterFilters);

        const groupFilters = controls.filter(
          (control) => control.tp_ctrl === "SnGroupLabel"
        );
        setGroups(groupFilters);

        const requiredsFilter = controls.filter(
          (control) => control.yn_required === "Y"
        );
        let tempreq = Object.assign({});
        requiredsFilter.map((req) => {
          tempreq[req.cd_param] = req.nm_label;
        });
        setRequireds(tempreq);
        const visibleFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() == "Y"
        );
        setVisibleControls(visibleFilters);
        const hiddenFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() != "Y"
        );
        setHiddenControls(hiddenFilters);
      } catch (error: any) {}
    };

    const fetchData = async () => {
      try {
        const mergedData = { ...data, ...{ cd_user_amd: userInfo.cd_user } };
        setChangeCon(mergedData);
        setFormData(mergedData);
        formDataRef.current = mergedData;
        setChangeData(undefined);
        changeDataRef.current = undefined;
        // autoFocus 를 끈 폼은 포커스를 가져오지 않는다(화면 아래 폼이 조회·신규마다 화면을 끌어내리는 것 방지).
        if (autoFocus) setFocus(true);
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchData();
    }, [data]);

    useEffect(() => {
      if (focus) {
        requestAnimationFrame(() => {
          focusInputByIndex(0);
          setFocus(false);
        });
      }
    }, [focus]);

    useEffect(() => {
      fetchControl();
    }, [controls]);

    useEffect(() => {
      if (formDataRef.current) {
        setChangeCon(formDataRef.current);
      }
    }, [editCons, filters, visibleControls]);

    useEffect(() => {
      if (groups) {
        const first = groups[0]?.no_line;
        if (first) {
          groupButtonClick(`${first}`);
        }
      }
    }, [groups, refs]);

    const onTextValueChanged = useCallback(
      async (param: string, data: string) => {
        const baseFormData = { ...formDataRef.current, [param]: data };
        // setFormData(baseFormData);
        setChangeCon(baseFormData);

        const prevValue = formDataRef.current?.[param] ?? "";
        if (prevValue === data) return;

        const updatedFields =
          (await onValueChanged?.(data, param, baseFormData)) || {};
        const finalFormData = { ...baseFormData, ...updatedFields };

        setFormData(finalFormData);
        formDataRef.current = finalFormData;
        setChangeCon(finalFormData);
        const newChangeData = {
          ...changeDataRef.current,
          [param]: data,
          ...updatedFields,
        };
        setChangeData(newChangeData);
        changeDataRef.current = newChangeData;
      },
      [formData]
    );

    const onNumberValueChanged = useCallback(
      async (param: string, data: number) => {
        const baseFormData = { ...formDataRef.current, [param]: data };
        // setFormData(baseFormData);
        setChangeCon(baseFormData);

        const prevValue = formDataRef.current?.[param] ?? 0;
        if (prevValue === data) return;

        const updatedFields =
          (await onValueChanged?.(data, param, baseFormData)) || {};
        const finalFormData = { ...baseFormData, ...updatedFields };

        setFormData(finalFormData);
        formDataRef.current = finalFormData;
        setChangeCon(finalFormData);
        const newChangeData = {
          ...changeData,
          [param]: data,
          ...updatedFields,
        };
        setChangeData(newChangeData);
        changeDataRef.current = newChangeData;
      },
      [formData]
    );

    const onPeriodValueChanged = useCallback(
      (param: string, start: string, end: string) => {
        const [startKey, endKey] = param.split(",").map((p) => p.trim());
        const prevStart = formDataRef.current?.[startKey] ?? "";
        const prevEnd = formDataRef.current?.[endKey] ?? "";

        if (prevStart === start && prevEnd === end) return;

        const updatedFormData = {
          ...formDataRef.current,
          [startKey]: start,
          [endKey]: end,
        };

        setFormData(updatedFormData);
        formDataRef.current = updatedFormData;
        setChangeCon(updatedFormData);
        const newChangeData = {
          ...changeData,
          [startKey]: start,
          [endKey]: end,
        };
        setChangeData(newChangeData);
        changeDataRef.current = newChangeData;
      },
      [formData]
    );

    const onCodeValueChanged = useCallback(
      async (
        code: string,
        data: string,
        name: string,
        data2: string,
        rowData?: string
      ) => {
        let baseFormData = {
          ...formDataRef.current,
          [code]: data,
          [name]: data2,
        };
        // setFormData(baseFormData);
        setChangeCon(baseFormData);

        let updatedFields = {};
        if (rowData) {
          updatedFields =
            (await onValueChanged?.(rowData, code, baseFormData)) || {};

          baseFormData = {
            ...baseFormData,
            ...updatedFields,
          };
        }

        setFormData(baseFormData);
        formDataRef.current = baseFormData;
        setChangeCon(baseFormData);
        const newChangeData = {
          ...changeData,
          [code]: data,
          [name]: data2,
          ...updatedFields,
        };
        setChangeData(newChangeData);
        changeDataRef.current = newChangeData;
      },
      [formData]
    );

    const setChangeCon = useCallback(
      (change: any) => {
        const editObj: Record<string, any> = {};
        const filterObj: Record<string, any> = {};
        const visibleObj: Record<string, boolean> = {};

        // editCon 처리
        if (editCons && change) {
          editCons.forEach((con) => {
            let conData = con.dc_editcon;

            if (change.state === "insert") {
              conData = conData.replace("new", "false");
            } else {
              conData = conData.replace("new", "true");
            }

            Object.keys(change).forEach((key) => {
              conData = conData.replaceAll(key, change[key]);
            });

            editObj[con.cd_param] = conData;
          });
          setEditCon(editObj);
        }

        // filter 처리
        if (filters && change) {
          filters.forEach((fil) => {
            let filData = fil.dc_filter;
            Object.keys(change).forEach((key) => {
              filData = filData.replaceAll(`{${key}}`, change[key]);
            });
            filterObj[fil.cd_param] = filData;
          });
          setFilter(filterObj);
        }

        // visible 처리
        if (visibleControls) {
          visibleControls.forEach((control) => {
            const raw = editObj?.[control.cd_param];
            if (raw && change.hasOwnProperty(control.cd_param)) {
              try {
                visibleObj[control.cd_param] = Boolean(
                  new Function(`return ${raw};`)()
                );
              } catch (e) {
                visibleObj[control.cd_param] = false;
                console.error("eval error in setChangeCon:", raw, e);
              }
            } else {
              visibleObj[control.cd_param] = false;
            }
          });
        }

        // 필요시 visibleObj 저장 또는 상태 처리 가능
      },
      [editCons, filters, visibleControls]
    );

    const setReadOnly = useCallback(
      (param: string) => {
        const expr = editCon?.[param];
        if (!expr) return false;

        try {
          return Boolean(new Function(`return (${expr});`)());
        } catch (e) {
          console.error("eval error in setReadOnly:", expr, e);
          return false;
        }
      },
      [editCon]
    );

    const addRow = useCallback(
      (formData: any) => {
        setFormData({ ...formData, cd_user_reg: userInfo.cd_user });
        formDataRef.current = { ...formData, cd_user_reg: userInfo.cd_user };
      },
      [formData]
    );

    useImperativeHandle(ref, () => ({
      getFormdata: () => formDataRef.current,
      getChangeData: () => changeData,
      focusInputByIndex,
      addRow: (formData: any) => addRow(formData),
      applyPopupConditions: (popupConditions: any) =>
        setPopupCons(popupConditions),
    }));

    const gridContainerStyle = {
      display: "grid",
      gridTemplateColumns: popupRef
        ? fitToPopup
          ? "repeat(6, minmax(0, 1fr))"
          : "repeat(6, minmax(125px, 1fr))"
        : gridTemplateColumns || "repeat(13, minmax(85px, 1fr))",
      gridAutoRows: fitToPopup ? "32px" : undefined,
      alignContent: fitToPopup ? "start" : undefined,
      rowGap: "10px",
      columnGap: fitToPopup ? "24px" : "10px",
      padding: "11px",
      overflow: "auto" as "auto",
    } as const;

    // 확인/취소 버튼 영역을 제외한 팝업 가용 높이를 계산한다.
    const getPopupFitHeight = () => {
      const popupRoot: HTMLElement | null =
        popupRef?.current?.getPopupParent?.() ?? null;
      if (!popupRoot) return expandedMaxHeight;
      return Math.max(260, popupRoot.clientHeight - 30);
    };

    useEffect(() => {
      // 옵션이 켜진 화면에서만 팝업 리사이즈에 따라 폼 높이를 동기화한다.
      if (!fitToPopup) return;
      const popupRoot: HTMLElement | null =
        popupRef?.current?.getPopupParent?.() ?? null;
      if (!popupRoot) return;

      const applyFit = () => {
        if (isExpand) return;
        setPanelHeight(getPopupFitHeight());
      };

      applyFit();
      const observer = new ResizeObserver(applyFit);
      observer.observe(popupRoot);
      return () => observer.disconnect();
    }, [fitToPopup, popupRef, isExpand, expandedMaxHeight]);

    const moveControl = (fromIdx: number, toIdx: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(fromIdx, 1); // 1. 기존 위치에서 제거
        updated.splice(toIdx, 0, moved); // 2. 목표 인덱스에 삽입
        return updated;
      });
    };

    const removeControl = (Idx: number, Id: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        updated.splice(Idx, 1); // 1. 기존 위치에서 제거
        return updated;
      });

      // controls에서 no_line 값이 Id와 같은 control을 찾아 hiddenControls에 추가
      const controlToHide = controls.find((control) => control.no_line === Id);
      if (controlToHide) {
        setHiddenControls((prev) => [...prev, controlToHide]);
      }
    };

    const handleExpand = () => {
      if (!isExpand) setPanelHeight(42);
      else setPanelHeight(fitToPopup ? getPopupFitHeight() : expandedMaxHeight);
      setIsExpand(!isExpand);
    };

    const handleRightClick = (event: MouseEvent) => {
      event.preventDefault(); // 기본 메뉴 비활성화

      // 자식 요소 위에서 우클릭을 했을 때는 아무 동작도 하지 않음
      if (formRef.current) {
        const panelArea = formRef.current.getBoundingClientRect(); // panel 영역
        const { clientX: x, clientY: y } = event; // 클릭 좌표

        // 클릭 좌표가 panel 영역 내에 있는지 확인
        const isInsidePanel =
          x >= panelArea.left &&
          x <= panelArea.right &&
          y >= panelArea.top &&
          y <= panelArea.bottom;

        if (isInsidePanel) {
          // 모든 자식 요소의 위치를 가져와서 클릭된 위치와 비교
          const children = Array.from(formRef.current.children);
          const isOnChild = children.some((child) => {
            const childRect = (child as HTMLElement).getBoundingClientRect();
            return (
              x >= childRect.left &&
              x <= childRect.right &&
              y >= childRect.top &&
              y <= childRect.bottom
            );
          });

          if (!isOnChild && canOpenMenu) {
            // 자식 요소 위가 아닌 빈 공간 클릭 -> 메뉴 열기. 보여 줄 항목이 없으면 띄우지 않는다 (2026-10-02)
            setMenuPosition({ mouseX: x, mouseY: y });
          }
        } else {
          handleMenuClose(); // panel 외부에서 클릭하면 메뉴 닫기
        }
      }
    };

    const handleMenuClose = () => {
      setMenuPosition(null);
    };

    const setFormControl = () => {
      setIsDragOn((prev) => !prev); // 상태로 토글 → 리렌더 보장
      handleMenuClose();
    };

    /* 항목 선택 — 조회조건과 같은 체크 창을 띄운다. 전에는 창 없이 편집 모드만 꺼졌다 (2026-10-06).
       필수 항목은 체크를 풀 수 없다 — X 로 지울 수 없는 것과 같다. 숨기면 입력할 칸이 없어 저장할 수 없다 */
    const addFormControl = () => {
      const Controls = [
        ...visibleControls.map((c) => ({ ...c, fg_visible: "Y" })),
        ...hiddenControls.map((c) => ({ ...c, fg_visible: "N" })),
      ];
      showPopupModal({
        title: "항목 선택",
        okayBtn: false,
        closeBtn: false,
        width: "250px",
        height: "400px",
        children: (
          <SnControlSelector
            initialControls={Controls}
            isLocked={(c) => c.yn_required === "Y"}
            onApply={(visible, hidden) => {
              setVisibleControls(visible || []);
              setHiddenControls(hidden || []);
            }}
          />
        ),
      });
      handleMenuClose();
    };

    const saveFormControl = async () => {
      const hasCdModule = controls.some(
        (control) =>
          control.cd_module !== undefined && control.cd_module !== null
      );
      const isNew = controls.some(
        (control) => control.cd_user !== userInfo?.cd_user
      );

      const mapControl = (control: any, idx: number, visible: boolean) => ({
        cd_firm: control.cd_firm,
        cd_menu: control.cd_menu,
        cd_form: control.cd_form,
        no_line: control.no_line,
        cd_user: userInfo?.cd_user,
        qt_rowspan: control.qt_rowspan,
        qt_colspan: control.qt_colspan,
        fg_visible: visible ? "Y" : "N",
        no_seq: visible ? idx + 1 : visibleControls.length + idx + 1,
        ...(isNew && {
          tp_ctrl: control.tp_ctrl,
          nm_label: control.nm_label,
          cd_value: control.cd_value,
          tp_format: control.tp_format,
          cd_param: control.cd_param,
          cd_clas: control.cd_clas,
          dc_sql: control.dc_sql,
          cd_popup: control.cd_popup,
          yn_required: control.yn_required,
          dc_editcon: control.dc_editcon,
          cd_displaycolumn: control.cd_displaycolumn,
          dc_filter: control.dc_filter,
        }),
        ...(hasCdModule && { cd_module: control.cd_module }),
      });

      const vControls = visibleControls.map((control, idx) =>
        mapControl(control, idx, true)
      );
      const hControls = hiddenControls.map((control, idx) =>
        mapControl(control, idx, false)
      );

      const updatedControls = [...vControls, ...hControls];
      const save = isNew
        ? { insert: updatedControls }
        : { update: updatedControls };

      const SaveData = !hasCdModule
        ? { sys_formctrl: save }
        : { sys_popupctrl: save };

      handleMenuClose();
      /* 저장되면 보통 모드로 돌아간다. 전에는 저장 뒤에도 편집 모드로 남아 [취소]를 한 번 더 눌러야 했다 (2026-10-02) */
      const saved = await fnSave(
        !hasCdModule
          ? "SN_SYS_BAS_REG_006_SAVE_01"
          : "SN_SYS_BAS_REG_008_SAVE_01",
        SaveData
      );
      if (saved !== false) {
        layoutSnapshotRef.current = null;
        setIsDragOn(false);
      }
    };

    /* 배치 편집은 SYSTEM 계정만. 편집 중이면 늘 메뉴가 있다.
       보여 줄 항목이 없으면(일반 사용자 평소) 메뉴를 띄우지 않는다 (2026-10-02 — 쓰지 않던 Option 1 · 2 · 3 삭제) */
    const canEditLayout = global.getState().userInfo.cd_user === "SYSTEM";
    const canOpenMenu = isDragOn || canEditLayout;

    const handleMenuRightClick = (event: React.MouseEvent) => {
      event.preventDefault();
      handleMenuClose();
      handleRightClick(event);
    };

    const groupButtonClick = (index: string) => {
      const target = refs.current[index];
      if (target && formRef.current) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    };

    const setRef = (key: string, node: HTMLDivElement | null) => {
      if (node) {
        refs.current[key] = node; // key를 기준으로 DOM 요소를 저장
      }
    };

    const setInputRef = (
      el: HTMLElement | null,
      index: number,
      isReadOnly: boolean
    ) => {
      inputEls.current[index] = {
        el,
        isFocusable: !isReadOnly,
      };
    };

    const focusInputByIndex = async (startIndex: number) => {
      let index = startIndex;

      while (index < inputEls.current.length) {
        const input = inputEls.current[index]?.el;
        const isFocusable = inputEls.current[index].isFocusable;
        if (input && isFocusable) {
          input.focus();
          break;
        }
        index++;
      }
    };

    const handleMoveFocus = (fromIndex: number, target: string) => {
      if (target === "current") {
        const el = inputEls.current?.[fromIndex].el;
        if (el) {
          el.focus();
        }
      } else if (target === "next") {
        const list = inputEls.current;
        for (let i = fromIndex + 1; i < list.length; i++) {
          const entry = list[i];
          if (entry?.el && entry.isFocusable) {
            entry.el.focus();
            return;
          }
        }

        // 끝까지 없으면 현재 blur
        list[fromIndex]?.el?.blur?.();
      }
    };

    const renderFreeFormContent = () => {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid rgb(186,191,199)",
          }}
        >
          <div className={"flex justify-start"}>
            {groups != null &&
              groups.length > 0 &&
              groups.map((group) => {
                return (
                  <div>
                    <SnNavBtn
                      text={group.nm_label}
                      onClick={() => groupButtonClick(`"${group.no_line}"`)}
                    ></SnNavBtn>
                  </div>
                );
              })}
          </div>

          <div
            className={expandcss}
            style={
              // fitToPopup 모드에서는 실제 height를 채우고,
              // 기본 모드에서는 기존 maxHeight 동작을 유지한다.
              fitToPopup
                ? { ...gridContainerStyle, height: `${panelHeight}px` }
                : { ...gridContainerStyle, maxHeight: `${panelHeight}px` }
            }
            onContextMenu={handleRightClick}
            ref={formRef}
          >
            {/* 배치 편집 — 끄는 동안은 이웃 칸만 비켜 나고, 순서는 손을 놓을 때 한 번 바뀐다 (2026-10-06, react-dnd → dnd-kit) */}
            {isDragOn ? (
              <SnSortableFormArea
                ids={(visibleControls || []).map(sortIdOf)}
                onMove={moveControl}
              >
                <>
                  {visibleControls != null &&
                    visibleControls.length > 0 &&
                    visibleControls.map((control, index) => (
                      <SnSortableFormControl
                        key={sortIdOf(control)}
                        sortId={sortIdOf(control)}
                        labletype="FreeForm"
                        control={control}
                        id={control.no_line}
                        index={index}
                        formData={formData}
                        requireds={requireds}
                        popupCons={popupCons}
                        setRef={setRef}
                        setInputRef={setInputRef}
                        handleMoveFocus={handleMoveFocus}
                        onTextValueChanged={onTextValueChanged}
                        onNumberValueChanged={onNumberValueChanged}
                        onPeriodValueChanged={onPeriodValueChanged}
                        onCodeValueChanged={onCodeValueChanged}
                        setReadOnly={setReadOnly}
                        onButtonClick={(param: string) => {
                          onButtonClick != null && onButtonClick(param);
                        }}
                        removeControl={removeControl}
                        filter={filter}
                        isFormPanel={true}
                      />
                    ))}
                </>
              </SnSortableFormArea>
            ) : (
              <>
                {visibleControls != null &&
                  visibleControls.length > 0 &&
                  visibleControls.map((control, index) => (
                    <SnFormControls
                      labletype="FreeForm"
                      control={control}
                      index={index}
                      formData={formData}
                      requireds={requireds}
                      popupCons={popupCons}
                      setRef={setRef}
                      setInputRef={setInputRef}
                      handleMoveFocus={handleMoveFocus}
                      onTextValueChanged={onTextValueChanged}
                      onNumberValueChanged={onNumberValueChanged}
                      onPeriodValueChanged={onPeriodValueChanged}
                      onCodeValueChanged={onCodeValueChanged}
                      setReadOnly={setReadOnly}
                      onButtonClick={(param: string) => {
                        onButtonClick != null && onButtonClick(param);
                      }}
                      filter={filter}
                      isFormPanel={true}
                    />
                  ))}
              </>
            )}
            <Menu
              open={menuPosition !== null}
              onClose={handleMenuClose}
              onContextMenu={handleMenuRightClick}
              anchorReference="anchorPosition"
              anchorPosition={
                menuPosition === null
                  ? undefined
                  : { top: menuPosition.mouseY, left: menuPosition.mouseX }
              }
            >
              {/* 우클릭 메뉴 (2026-10-02) — 색 아이콘 · 제목 줄. 평소 [배치 편집](SYSTEM), 편집 중 [항목 선택 · 저장 · 편집 취소].
                  동작은 전과 같고 이름 · 모양만 바꿨다 */}
              {!isDragOn && canEditLayout && [
                <ListSubheader key="h" sx={menuHeadSx}>폼</ListSubheader>,
                <MenuItem key="edit" dense onClick={() => {
                    layoutSnapshotRef.current = { visible: visibleControls, hidden: hiddenControls };
                    setFormControl();
                  }}>
                  <ListItemIcon><TuneIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText>배치 편집</ListItemText>
                </MenuItem>,
              ]}
              {isDragOn && [
                <ListSubheader key="h" sx={menuHeadSx}>폼 편집</ListSubheader>,
                <MenuItem key="pick" dense onClick={() => addFormControl()}>
                  <ListItemIcon><PlaylistAddCheckIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText>항목 선택</ListItemText>
                </MenuItem>,
                <MenuItem key="save" dense onClick={() => saveFormControl()}>
                  <ListItemIcon><SaveIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText>저장</ListItemText>
                </MenuItem>,
                <Divider key="d" />,
                <MenuItem key="cancel" dense onClick={() => {
                    /* 편집 취소 — 배치 편집 전으로 되돌리고 보통 모드로 (2026-10-02) */
                    const snap = layoutSnapshotRef.current;
                    layoutSnapshotRef.current = null;
                    if (snap) {
                      setVisibleControls(snap.visible);
                      setHiddenControls(snap.hidden);
                    }
                    setFormControl();
                  }}>
                  <ListItemIcon><CloseIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>편집 취소</ListItemText>
                </MenuItem>,
              ]}
            </Menu>
          </div>

          <div className="flex justify-center h-[15px]">
            <IconButton
              onClick={handleExpand}
              sx={{
                color: "gray",
                "&:hover": {
                  color: "blue",
                  transform: "scale(1.2)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              {!isExpand && <ArrowDropUpIcon />}
              {isExpand && <ArrowDropDownIcon />}
            </IconButton>
          </div>
        </div>
      );
    };

    return (
      <>
        {title ? (
          <SnTab
            titles={[title]}
            width={"100%"}
            height={"auto"}
            header={[<SnTabHeader body={header} />]}
            body={[renderFreeFormContent()]}
          />
        ) : (
          renderFreeFormContent()
        )}
      </>
    );
  }
);
export default React.memo(FreeForm);
